import React, { useEffect, useState } from "react";
import { useLocalization } from "../../contexts/LocalizationContext";
import { merchantService, MerchantProfile } from "../../services/merchantService";
import LoadingScreen from "../LoadingScreen";
import {
  Key,
  Copy,
  CheckCircle,
  Eye,
  EyeOff,
  RefreshCw,
  Shield,
  Code2,
  Globe,
} from "lucide-react";

const API_BASE_URL = "https://paydrc.gofreshbakery.net/api/v5/";

const ApiKeys: React.FC = () => {
  const { t } = useLocalization();
  const [profile, setProfile] = useState<MerchantProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSecret, setShowSecret] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const data = await merchantService.getMerchantProfile();
      setProfile(data);
    } catch {
      setError(t("merchant.errorLoadingProfile") || "Failed to load credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text: string, kind: string) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(kind);
    setTimeout(() => setCopySuccess(null), 2000);
  };

  const handleRegenerate = async () => {
    if (!confirm(t("merchant.confirmRegenerate") || "Are you sure? The current secret will be invalidated immediately.")) {
      return;
    }
    setIsRegenerating(true);
    try {
      const result = await merchantService.regenerateSecret();
      alert(`${t("merchant.newSecretGenerated") || "New secret"}: ${result.merchant_secrete}`);
      await loadProfile();
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || "";
      alert(`${t("merchant.errorRegenerateSecret") || "Could not regenerate secret"}${detail ? ` — ${detail}` : ""}`);
    } finally {
      setIsRegenerating(false);
    }
  };

  if (loading) return <LoadingScreen />;
  if (error || !profile) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700 text-sm">{error || "Profile not available."}</div>
      </div>
    );
  }

  const apiExample = JSON.stringify(
    {
      merchant_id: profile.merchant.merchant_id,
      merchant_secrete: profile.merchant.merchant_secrete || "********",
      amount: "xxxxxx",
      currency: "xxxxxx",
      action: "xxxxxx",
      customer_number: "xxxxxx",
      firstname: profile.user.firstname,
      lastname: profile.user.lastname,
      "e-mail": profile.user.email,
      reference: "xxxxxx",
      method: "xxxxxx",
      callback_url: "xxxxxx",
    },
    null,
    2,
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 space-y-6">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 shadow-xl shadow-indigo-500/15">
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-indigo-500/25 blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-1/3 w-56 h-56 rounded-full bg-cyan-400/15 blur-3xl pointer-events-none" />
          <div className="relative p-5 sm:p-6 flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center flex-shrink-0">
              <Key className="w-5 h-5 text-indigo-300" />
            </div>
            <div className="min-w-0">
              <div className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] bg-indigo-500/15 text-indigo-200 ring-1 ring-indigo-400/25 mb-2">
                Developers
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">API Keys</h1>
              <p className="text-slate-300 text-xs sm:text-sm mt-1">
                Identifiants d'integration PayDRC. Garde le secret prive et ne le partage qu'avec des systemes proteges.
              </p>
            </div>
          </div>
        </div>

        {/* Base URL */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
                <Globe className="w-3.5 h-3.5" /> Base URL
              </div>
              <div className="font-mono text-sm text-slate-900 break-all">{API_BASE_URL}</div>
            </div>
            <button
              onClick={() => handleCopy(API_BASE_URL, "base_url")}
              className="p-2.5 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg border border-slate-200 transition-colors"
            >
              {copySuccess === "base_url" ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Merchant ID + Code */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Merchant ID</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={profile.merchant.merchant_id}
                readOnly
                className="flex-1 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 font-mono focus:outline-none"
              />
              <button
                onClick={() => handleCopy(profile.merchant.merchant_id, "merchant_id")}
                className="p-2.5 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg border border-slate-200 transition-colors"
              >
                {copySuccess === "merchant_id" ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Merchant Code</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={profile.merchant.merchant_code}
                readOnly
                className="flex-1 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 font-mono focus:outline-none"
              />
              <button
                onClick={() => handleCopy(profile.merchant.merchant_code, "merchant_code")}
                className="p-2.5 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg border border-slate-200 transition-colors"
              >
                {copySuccess === "merchant_code" ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* Secret */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500">Merchant Secret</label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowSecret((s) => !s)}
                className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-indigo-600"
              >
                {showSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {showSecret ? "Hide" : "Show"}
              </button>
              <button
                onClick={handleRegenerate}
                disabled={isRegenerating}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60 text-xs font-semibold transition-colors"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isRegenerating ? "animate-spin" : ""}`} />
                {isRegenerating ? "Regenerating..." : "Regenerate"}
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type={showSecret ? "text" : "password"}
              value={profile.merchant.merchant_secrete || ""}
              readOnly
              className="flex-1 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 font-mono focus:outline-none"
            />
            <button
              onClick={() => handleCopy(profile.merchant.merchant_secrete || "", "merchant_secret")}
              className="p-2.5 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg border border-slate-200 transition-colors"
            >
              {copySuccess === "merchant_secret" ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          {profile.merchant.updated_at && (
            <p className="text-[11px] text-slate-500 mt-2">
              Last updated: {new Date(profile.merchant.updated_at).toLocaleString()}
            </p>
          )}
        </div>

        {/* Code sample */}
        <div className="bg-slate-900 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold text-emerald-300">
              <Code2 className="w-3.5 h-3.5" />
              API Request Example
            </div>
            <button
              onClick={() => handleCopy(apiExample, "api_example")}
              className="text-emerald-300 hover:text-emerald-200 text-xs font-semibold"
            >
              {copySuccess === "api_example" ? "Copied" : "Copy"}
            </button>
          </div>
          <pre className="text-emerald-300 text-xs overflow-x-auto leading-relaxed">{`POST ${API_BASE_URL}\n${apiExample}`}</pre>
        </div>

        {/* Security */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex gap-3">
          <Shield className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 leading-relaxed">
            <div className="font-semibold mb-1">{t("merchant.securityNotice") || "Garde ces credentials en securite"}</div>
            <ul className="list-disc pl-5 space-y-0.5 text-xs">
              <li>{t("merchant.securityTip1") || "Ne committe jamais le secret dans un repo Git."}</li>
              <li>{t("merchant.securityTip2") || "N'expose pas le secret cote client (browser, mobile bundle)."}</li>
              <li>{t("merchant.securityTip3") || "Stocke le secret dans un vault ou un manager de secrets cote serveur."}</li>
              <li>{t("merchant.securityTip4") || "Regenere immediatement si tu suspectes une fuite."}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiKeys;
